<template>
  <div class="flex flex-col gap-4 w-full">
    <c-card class="w-full flex flex-col p-5">
      <div class="grid grid-cols-3 gap-4 pt-6 pb-6">
        <DashboardCard
          title="Planned Leave"
          :value="summary.pending || 0"
          icon="file-checked-purple"
          background-color="#FCF4FF"
        />
        <DashboardCard
          title="Approved Requests"
          :value="summary.approved || 0"
          icon="file-checked-purple"
        />
        <DashboardCard
          title="Disapproved Requests"
          :value="summary.disapproved || 0"
          icon="file-dismiss"
        />
      </div>
      <template v-slot:footer>
        <CardFooter
          reloadcard
          search-placeholder="Search"
          @reload="reloadData()"
          @searchResult="onQueryFn({ search: $event }, null, getDelegatedRequests)"
        />
      </template>
    </c-card>

<c-table
      :headers="headers"
      :items="delegatedRequests"
      aria-label="annual leave plan table"
      :has-number="false"
      :loading="isFetching"
      v-if="delegatedRequests.length || isFetching"
    >
        <template v-slot:item="{ item }">
            <div class="flex items-center gap-3" v-if="item.delegator">
                <img
                    v-if="item.data.delegator.photo"
                    :src="item.data.delegator.photo"
                    alt="Delegator Photo"
                    class="w-8 h-8 rounded-full object-cover"
                />
                <div class="flex flex-col">
                    <span class="text-sm font-medium text-darkPurple">
                    {{ item.data.delegator.fname }} {{ item.data.delegator.lname }}
                    </span>
                    <span class="text-xs text-gray-500">
                    {{ item.data.delegator.userDesignation.name }}
                    </span>
                </div>
            </div>
            <span v-if="item.category" class="text-darkPurple text-sm font-normal">
                {{ item.data.category.name }}
            </span>
            <span v-if="item.requestStartDate" class="text-darkPurple text-sm font-normal">
                {{ item.data.requestStartDate ? $DATEFORMAT(new Date(item.data.requestStartDate), 'MMMM dd, yyyy') : '' }}
            </span>
            <div v-if="item.approvalStatus" class="rounded-md p-2 text-sm w-28 text-center capitalize"
              :class="{
                'bg-green-100 text-mediumSeaGreen': item.data.approvalStatus === 'approved',
                'bg-red-100 text-desire': item.data.approvalStatus === 'disapproved',
                'bg-yellow-100 text-carrotOrange': item.data.approvalStatus === 'pending',
                'bg-red-100 text-flame': item.data.approvalStatus === 'cancelled',
              }">
              {{ item.data.approvalStatus }}
            </div>
            <div v-if="item.id">
              <c-menu left @click.stop="">
                <template v-slot:title>
                  <c-icon icon-name="more_icon" size="xs" />
                </template>
                <div class="flex flex-col w-52 h-18 p-2 justify-start items-start">
                  <div class="w-full" @click="$refs.viewDelegatedRequestRef.onOpen({leaveInfo: item.data, employeeInfo: item.data.delegator })">
                    <div class="hover:bg-ghostWhite w-full rounded-md">
                      <div class="flex justify-start items-center p-2 gap-2 cursor-pointer">
                        <c-icon icon-name="icon-eye" class-name="text-darkPurple" size="xs" />
                        <span class="text-darkPurple text-sm">View Handover Note</span>
                      </div>
                    </div>
                  </div>
                </div>
              </c-menu>
            </div>
        </template>
    </c-table>

    <div v-else class="flex flex-col justify-center items-center gap-0 px-10">
      <c-icon icon-name="paygrade_Illustration" size="" class-name="w-56 h-56" />
      <p class="font-normal text-base leading-5 text-jet text-center w-1/2">
        Delegated requests can be found here. This is empty now,
        but delegations will appear here when you are assigned as a delegatee.
      </p>
    </div>

    <ViewDelegatedRequest ref="viewDelegatedRequestRef" />
  </div>
</template>

<script>
import CTable from "@scelloo/cloudenly-ui/src/components/table";
import CCard from "@scelloo/cloudenly-ui/src/components/card";
import CMenu from "@scelloo/cloudenly-ui/src/components/Menu";
import CIcon from "@/components/Icon";
import CardFooter from "@/components/CardFooter";
import DashboardCard from "@/components/DashboardCard";
import Loader from "@scelloo/cloudenly-ui/src/components/loader";

export default {
  components: {
    CCard,
    CTable,
    CIcon,
    CMenu,
    CardFooter,
    DashboardCard,
    Loader,
    ViewDelegatedRequest: () => import("./sideActions/ViewDelegatedRequest"),
  },
  data() {
    return {
      isFetching: false,
      isLoading: true,
      isCancellingRequest: false,
      isOpenCancelModal: false,
      selectedItem: {},
      employeeInfo: {
        fname: "John",
        lname: "Doe",
      },
      delegatedRequests: [],
      pendingRequests: [],
      approvedRequests: [],
      disapprovedRequests: [],
      summary: {
        pending: 0,
        approved: 0,
        disapproved: 0,
        total: 0,
      },
      setQueryTimeout: null,
      queryParams: {},
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 4,
        last_page: 1,
      },
      headers: [
        { title: "Delegator", value: "delegator" },
        { title: "Leave Type", value: "category" },
        { title: "Duration", value: "requestStartDate" },
        { title: "Status", value: "approvalStatus" },
        { title: "", value: "id", image: true },
      ],
    };
  },
  props: {
    mode: {
      type: String,
      default: "admin",
    },
  },
  methods: {
    onQueryFn(queryParams, payload, callback) {
      clearTimeout(this.setQueryTimeout);
      this.setQueryTimeout = setTimeout(() => {
        if (queryParams === undefined) {
          this.queryParams = {};
          return callback({ ...this.queryParams, ...payload });
        }
        this.queryParams = { ...this.queryParams, ...queryParams };
        return callback({ ...this.queryParams, ...payload });
      }, 1000);
    },
    async getDelegatedRequests(params) {
      this.isFetching = true;
      const payload = { userId: this.$AuthUser.id, params };
      await this.$_delegatedRequests(payload)
        .then(({ data }) => {
          const rawData = data.employees || [];

          this.delegatedRequests = rawData.map((item) => {
            const mappedItem = {
              id: item.requestId,
              delegator: item.employee,
              category: { name: item.leaveType },
              requestStartDate: item.from,
              requestEndDate: item.to,
              daysRequested: item.daysRequested,
              approvalStatus: item.status,
              createdAt: item.createdAt,
              requestReason: item.details,
              handoverNote: item.handoverNote,
              fileUrl: item.fileUrl,
              fileName: item.fileName,
              fileSize: item.fileSize,
              fileMimeType: item.fileMimeType,
              allowanceIsRequested: item.allowanceIsRequested,
              delegatee: item.delegatee,
            };

            return {
              ...mappedItem,
              data: mappedItem,
            };
          });


          if (data.meta) {
            this.pagination = {
              current_page: data.meta.page,
              per_page: data.meta.to - data.meta.from + 1,
              total: data.meta.total,
              last_page: data.meta.lastPage,
            };
          }
          
          this.isFetching = false;
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.message || 
                              error?.message || 
                              "Error fetching data, please check your network.";
          this.$toasted.error(errorMessage, { duration: 3000 });
          this.isFetching = false;
        });
    },
    async getDelegationSummary() {
      const payload = { userId: this.$AuthUser.id };
      await this.$_delegationAnalyticsSummary(payload)
        .then(({ data }) => {
          if (data.summary) {
            this.summary = {
              pending: data.summary.pending || 0,
              approved: data.summary.approved || 0,
              disapproved: data.summary.disapproved || 0,
              total: data.summary.total || 0,
            };
          }
        })
        .catch(() => {
          // Silently fail for summary - don't show error toast
          // as it's not critical for the main functionality
        });
    },
    async reloadData() {
      await Promise.all([
        this.getDelegationSummary(),
        this.getDelegatedRequests(),
      ]);
    },
    async bootstrapModule() {
      this.isLoading = true;
      await Promise.all([
        this.getDelegationSummary(),
        this.getDelegatedRequests(),
      ]);
      this.isLoading = false;
    },
  },
  created() {
    this.bootstrapModule();
  },
};
</script>

<style scoped>
::v-deep .select-class span select {
  margin-top: 3px !important;
  height: 42px !important;
}

.btn-bg {
  background-color: var(--dynamic-Background) !important;
}

.btn-border {
  color: var(--dynamic-Background) !important;
  border: 1px solid var(--dynamic-Background) !important;
}
</style>
